// Argus — TaskManagerWindow: process list + kill PID via WS direct.
// Bloc task-manager-services 2026-05-07.

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopIcon from '@mui/icons-material/Stop';
import ClearIcon from '@mui/icons-material/Clear';
import { TableVirtuoso } from 'react-virtuoso';
import { wsClient } from '../lib/wsClient';
import { dayjs } from '../lib/dayjsSetup';

interface ProcessRow {
  pid: number;
  parentPid: number;
  name: string;
  ramBytes: number;
  sessionId: number;
  userName: string | null;
  startTime: string | null;
}

type SortKey = 'pid' | 'name' | 'ramBytes' | 'sessionId' | 'userName' | 'startTime';
type SortDir = 'asc' | 'desc';

function formatRam(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function TaskManagerWindow({ agentId, agentOnline }: { agentId: string; agentOnline: boolean }): JSX.Element {
  const [processes, setProcesses] = useState<ProcessRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('ramBytes');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [killTarget, setKillTarget] = useState<ProcessRow | null>(null);
  const [killing, setKilling] = useState(false);
  const [killError, setKillError] = useState<string | null>(null);
  const [capturedAt, setCapturedAt] = useState<string | null>(null);
  const [selectedPid, setSelectedPid] = useState<number | null>(null);
  const theme = useTheme();
  const selectedBg = theme.palette.action.selected;

  const loadProcesses = async (): Promise<void> => {
    if (!agentOnline) {
      setError('Agent offline.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await wsClient.requestProcessList(agentId);
      setProcesses(result.processes as ProcessRow[]);
      setCapturedAt(result.capturedAt);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load processes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProcesses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, agentOnline]);

  const handleSort = (key: SortKey): void => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'userName' ? 'asc' : 'desc');
    }
  };

  const filtered = useMemo(() => {
    const f = filter.toLowerCase().trim();
    let arr = processes;
    if (f) {
      arr = arr.filter(
        (p) => p.name.toLowerCase().includes(f) ||
          String(p.pid).includes(f) ||
          (p.userName?.toLowerCase().includes(f) ?? false),
      );
    }
    const sorted = [...arr].sort((a, b) => {
      const sign = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'pid': return (a.pid - b.pid) * sign;
        case 'name': return a.name.localeCompare(b.name) * sign;
        case 'ramBytes': return (a.ramBytes - b.ramBytes) * sign;
        case 'sessionId': return (a.sessionId - b.sessionId) * sign;
        case 'userName': return (a.userName ?? '').localeCompare(b.userName ?? '') * sign;
        case 'startTime': return (a.startTime ?? '').localeCompare(b.startTime ?? '') * sign;
      }
    });
    return sorted;
  }, [processes, filter, sortKey, sortDir]);

  const handleKill = async (force: boolean): Promise<void> => {
    if (!killTarget) return;
    setKilling(true);
    setKillError(null);
    try {
      await wsClient.requestProcessKill(agentId, killTarget.pid, force);
      setKillTarget(null);
      setSelectedPid(null);
      await loadProcesses();
    } catch (err) {
      setKillError(err instanceof Error ? err.message : 'Kill failed.');
    } finally {
      setKilling(false);
    }
  };

  const sortIndicator = (key: SortKey): string => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder="Filter by name / PID / user..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: 360 }}
          slotProps={{
            input: {
              endAdornment: filter !== '' ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setFilter('')} aria-label="Clear filter" edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, textAlign: 'right' }}>
          {filtered.length} / {processes.length} processes
          {capturedAt && ` · captured ${dayjs(capturedAt).fromNow()}`}
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => void loadProcesses()} disabled={loading || !agentOnline}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={selectedPid === null ? 'Select a process to enable Kill' : `Kill PID ${selectedPid}`}>
          <span>
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<StopIcon />}
              onClick={() => {
                const target = filtered.find((p) => p.pid === selectedPid) ?? null;
                if (target) setKillTarget(target);
              }}
              disabled={selectedPid === null || !agentOnline}
            >
              Kill
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert>}

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <TableVirtuoso
          data={filtered}
          fixedHeaderContent={() => (
            <tr style={{ background: 'var(--mui-palette-background-paper)' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--mui-palette-divider)', width: 80 }} onClick={() => handleSort('pid')}>PID{sortIndicator('pid')}</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--mui-palette-divider)' }} onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--mui-palette-divider)', width: 100 }} onClick={() => handleSort('ramBytes')}>RAM{sortIndicator('ramBytes')}</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--mui-palette-divider)', width: 200 }} onClick={() => handleSort('userName')}>User{sortIndicator('userName')}</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--mui-palette-divider)', width: 70 }} onClick={() => handleSort('sessionId')}>Sess{sortIndicator('sessionId')}</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--mui-palette-divider)', width: 140 }} onClick={() => handleSort('startTime')}>Started{sortIndicator('startTime')}</th>
            </tr>
          )}
          itemContent={(_idx, p) => {
            const isSelected = selectedPid === p.pid;
            return (
              <>
                <td
                  style={{
                    padding: '4px 8px', fontFamily: 'monospace',
                    background: isSelected ? selectedBg : undefined,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedPid(p.pid)}
                >{p.pid}</td>
                <td
                  style={{
                    padding: '4px 8px',
                    background: isSelected ? selectedBg : undefined,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedPid(p.pid)}
                >{p.name}</td>
                <td
                  style={{
                    padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace',
                    background: isSelected ? selectedBg : undefined,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedPid(p.pid)}
                >{formatRam(p.ramBytes)}</td>
                <td
                  style={{
                    padding: '4px 8px', color: 'var(--mui-palette-text-secondary)',
                    background: isSelected ? selectedBg : undefined,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedPid(p.pid)}
                >{p.userName || '—'}</td>
                <td
                  style={{
                    padding: '4px 8px', textAlign: 'right',
                    background: isSelected ? selectedBg : undefined,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedPid(p.pid)}
                >{p.sessionId}</td>
                <td
                  style={{
                    padding: '4px 8px', color: 'var(--mui-palette-text-secondary)', fontSize: '0.85em',
                    background: isSelected ? selectedBg : undefined,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedPid(p.pid)}
                >
                  {p.startTime ? dayjs(p.startTime).fromNow() : '—'}
                </td>
              </>
            );
          }}
        />
      </Box>

      <Dialog open={killTarget !== null} onClose={() => !killing && setKillTarget(null)}>
        <DialogTitle>Kill process</DialogTitle>
        <DialogContent>
          {killTarget && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Are you sure you want to terminate this process?
              </Typography>
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                <Typography variant="body2"><strong>PID:</strong> {killTarget.pid}</Typography>
                <Typography variant="body2"><strong>Name:</strong> {killTarget.name}</Typography>
                {killTarget.userName && (
                  <Typography variant="body2"><strong>User:</strong> {killTarget.userName}</Typography>
                )}
                <Typography variant="body2"><strong>Session:</strong> {killTarget.sessionId}</Typography>
              </Box>
              {killError && <Alert severity="error" sx={{ mb: 1 }}>{killError}</Alert>}
              <Typography variant="caption" color="text.secondary">
                Termination is forced (TerminateProcess). Unsaved data will be lost.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKillTarget(null)} disabled={killing} autoFocus variant="contained">Cancel</Button>
          <Button color="error" variant="outlined" onClick={() => void handleKill(false)} disabled={killing}>
            {killing ? 'Killing...' : 'Kill'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
